/**
 * Scan a key with the phone's camera. A key's QR is a link to
 * `<site>/code/<code>`; once one is read, its code is opened on this site,
 * the same landing a scan from the camera app reaches. jsQR decodes the
 * frames because iOS Safari has no BarcodeDetector.
 */
import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Check } from "lucide-react";
import { ENTRY } from "@/features/funnel/lib/copy";
import { keyCodeFrom } from "./entry";

/** How long "Key found" shows before the key's page opens. */
const FOUND_MS = 1400;

export function KeyScanner({
  onKey,
  onBack,
}: {
  onKey: (code: string) => void;
  onBack: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<"camera" | "notKey" | null>(null);
  // read: the frame holds on the key, a tick over it, then its page
  const [found, setFound] = useState(false);
  // the camera runs once per mount, whatever the parent re-renders
  const onKeyRef = useRef(onKey);
  useEffect(() => {
    onKeyRef.current = onKey;
  }, [onKey]);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    let done: ReturnType<typeof setTimeout> | undefined;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const scan = () => {
      const v = video.current;
      if (stopped || !v || !ctx) return;
      if (v.readyState >= v.HAVE_ENOUGH_DATA && v.videoWidth) {
        // a smaller frame decodes faster and a key fills most of it
        const scale = Math.min(1, 640 / v.videoWidth);
        canvas.width = Math.round(v.videoWidth * scale);
        canvas.height = Math.round(v.videoHeight * scale);
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qr = jsQR(img.data, img.width, img.height, {
          inversionAttempts: "dontInvert",
        });
        if (qr?.data) {
          const code = keyCodeFrom(qr.data);
          if (code) {
            stopped = true;
            v.pause();
            setError(null);
            setFound(true);
            done = setTimeout(() => onKeyRef.current(code), FOUND_MS);
            return;
          }
          setError("notKey");
        }
      }
      raf = requestAnimationFrame(scan);
    };
    // (no camera API at all: an insecure page, or a very old browser)
    const media = navigator.mediaDevices?.getUserMedia
      ? navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        })
      : Promise.reject(new Error("no camera"));
    media
      .then((s) => {
        if (stopped) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        const v = video.current;
        if (!v) return;
        v.srcObject = s;
        void v.play().catch(() => {});
        raf = requestAnimationFrame(scan);
      })
      .catch(() => setError("camera"));
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      clearTimeout(done);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="flex w-full max-w-[380px] flex-col items-center text-center">
      <h1 className="font-stencil text-[24px] tracking-[.04em] text-[#f4eddb] uppercase">
        {found ? ENTRY.scanDone : ENTRY.scanTitle}
      </h1>
      <p className="mt-2 text-[15px] text-[#a99d86]">
        {found ? ENTRY.scanDoneHint : ENTRY.scanHint}
      </p>
      <div className="relative mt-5 aspect-square w-full overflow-hidden rounded-[14px] border border-[#d6a94e]/45 bg-black">
        <video
          ref={video}
          muted
          playsInline
          className="size-full object-cover"
        />
        {/* the frame to aim with */}
        <span
          className={`pointer-events-none absolute inset-[16%] rounded-[10px] border-2 shadow-[0_0_0_999px_rgba(0,0,0,.35)] transition-colors ${found ? "border-[#7fd18b]" : "border-[#d6a94e]"}`}
        />
        {found ? (
          <div className="absolute inset-0 grid animate-[smFound_.35s_ease-out_both] place-items-center bg-black/45">
            <span className="grid size-20 place-items-center rounded-full bg-[#7fd18b] text-black shadow-[0_0_30px_rgba(127,209,139,.6)]">
              <Check className="size-11" strokeWidth={3} />
            </span>
          </div>
        ) : null}
      </div>
      {error ? (
        <p className="mt-4 text-[14.5px] leading-snug text-[#f0b3a8]">
          {error === "camera" ? ENTRY.noCamera : ENTRY.notAKey}
        </p>
      ) : null}
      <style>{`@keyframes smFound{from{opacity:0;transform:scale(.8)}to{opacity:1;transform:none}}`}</style>
      <button
        type="button"
        disabled={found}
        onClick={onBack}
        className="mt-5 cursor-pointer py-2 text-[15px] text-[#a99d86] underline underline-offset-2"
      >
        {ENTRY.back}
      </button>
    </div>
  );
}
